/**
 * Разбор справочника ПОРЦИЯМИ — слой TanStack Query (AK-546, ч.2).
 *
 * Тонкая обвязка над tenant-scoped API `api/v1/reference/catalog_review.py`
 * (ч.1). Типы — строго под DTO бэкенда; менять здесь только вслед за ним.
 *
 * Модель, если коротко (решения founder 07.08):
 *  · **порция** = источник + дата + состав, закрывается целиком (решение Б);
 *  · порция сначала **сверяется со справочником** — четыре числа
 *    «всего пришло / уже есть / платформа предложила / нужен человек»;
 *  · **предложение платформы решением НЕ является** (AK-545): пока человек не
 *    нажал «Согласен» / «Заменить» / «Не нужно», строка висит `pending`.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseInfiniteQueryResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@/lib/api';

const BASE = '/api/v1/reference/catalog-review';

/** Справочники, у которых есть разбор порциями. У каждого — свой экран. */
export type ReviewEntityType = 'crop' | 'operation_type';

/** Откуда пришла порция. `retro` — то, что тенант накопил до модели порций. */
export type BatchSourceKind =
  | 'agrohistory'
  | 'excel'
  | 'one_c'
  | 'manual'
  | 'retro';

export type BatchStatus = 'open' | 'in_review' | 'deferred' | 'completed';

/**
 * Бакет сверки — те самые четыре числа:
 *  · `existing`  — такое название в справочнике УЖЕ ЕСТЬ, разбирать нечего;
 *  · `suggested` — нового названия нет, но платформа поняла, чем оно является;
 *  · `manual`    — ни совпадения, ни уверенного предложения → нужен человек.
 */
export type ItemBucket = 'existing' | 'suggested' | 'manual';

export type ItemDecision = 'pending' | 'agreed' | 'replaced' | 'dismissed';

/**
 * «Чем занято поле?» (Р-5) — сиблинги под общим родителем + пятая дверь для
 * служебных строк чужой системы. У тех.операций дверей две.
 */
export type TargetKind =
  | 'crop'
  | 'crop_mix'
  | 'field_state'
  | 'land_category'
  | 'operation_type'
  | 'not_needed';

/** DTO `BatchCounts`. `needs_review` — ЧЕСТНЫЙ объём работы, прогресс от него. */
export interface BatchCounts {
  total: number;
  existing: number;
  suggested: number;
  manual: number;
  needs_review: number;
  decided: number;
  pending: number;
  /**
   * Сколько строк закроет ОДНО нажатие «Согласиться со всеми». Считается
   * сервером по тому же предикату, что и сама пачка, поэтому число на кнопке
   * можно брать только отсюда: длина загруженной СТРАНИЦЫ соврёт (страница
   * 100, а пачка закроет 380).
   */
  pending_suggested: number;
  /** Сколько строк реально ждёт человека на шаге «Нужен человек». */
  pending_manual: number;
}

/** DTO `BatchOut`. */
export interface ReviewBatch {
  id: string;
  company_id: string;
  entity_type: ReviewEntityType | string;
  source_kind: BatchSourceKind | string;
  source_label: string | null;
  source_ref: string | null;
  scope_level: string;
  scope_ref_id: string | null;
  status: BatchStatus | string;
  counts: BatchCounts;
  received_at: string;
  /**
   * Откуда названия РЕАЛЬНО приехали — UAT founder 08.08 («источник должен быть
   * реальный: интеграция АгроХистори, дата последней синхронизации»).
   * У накопленного до появления разбора `source_kind='retro'` — служебная
   * правда; происхождение и настоящие даты приходят вот здесь.
   */
  origin_kind: string | null;
  origin_from: string | null;
  origin_to: string | null;
  reconciled_at: string | null;
  started_at: string | null;
  completed_at: string | null;
}

/** DTO `ItemOut`. */
export interface ReviewItem {
  id: string;
  batch_id: string;
  raw_name: string;
  occurrence_count: number;
  first_year: number | null;
  last_year: number | null;
  bucket: ItemBucket | string;
  source_item_id: string | null;
  existing_item_id: string | null;
  suggested_kind: string | null;
  suggested_ref_id: string | null;
  suggested_goal_id: string | null;
  suggestion_confidence: number | null;
  decision: ItemDecision | string;
  decision_target_kind: string | null;
  decision_ref_id: string | null;
  resolved_item_id: string | null;
  decided_by_user_id: string | null;
  decided_at: string | null;
  disputed_at: string | null;
  dispute_note: string | null;
}

/** DTO `CatalogReviewSummary`. */
export interface ReviewSummary {
  entity_type: string;
  open_batches: number;
  pending_in_batches: number;
  /** Записей справочника, требующих разбора и НЕ покрытых ни одной порцией. */
  outside_batches: number;
}

/** DTO `CompleteOut`. Ощущение конца не подделываем — отсюда `fully_resolved`. */
export interface CompleteResult {
  status: string;
  fully_resolved: boolean;
  counts: BatchCounts;
  created_items: number;
}

/** DTO `DeclarationOut` — субъективная оценка админа (Р-2), не расчёт системы. */
export interface SufficiencyDeclaration {
  company_id: string;
  entity_type: string;
  state: 'sufficient' | 'needs_more' | null;
  note: string | null;
  declared_by_user_id: string | null;
  declared_at: string | null;
}

export interface ReplacePayload {
  target_kind: TargetKind;
  ref_id?: string | null;
  growing_goal_id?: string | null;
  /** Решение Г: у смеси спрашиваем ТОЛЬКО состав, без долей и норм высева. */
  component_crop_ids?: string[] | null;
  local_name?: string | null;
}

// ─── ключи кэша ──────────────────────────────────────────────────────────────

export const reviewKeys = {
  summary: (et: ReviewEntityType) => ['catalog-review', et, 'summary'] as const,
  batches: (et: ReviewEntityType) => ['catalog-review', et, 'batches'] as const,
  batch: (et: ReviewEntityType, id: string) =>
    ['catalog-review', et, 'batch', id] as const,
  items: (et: ReviewEntityType, id: string, bucket: string) =>
    ['catalog-review', et, 'items', id, bucket] as const,
  declaration: (et: ReviewEntityType, companyId: string | null) =>
    ['catalog-review', et, 'declaration', companyId] as const,
};

// ─── чтение ──────────────────────────────────────────────────────────────────

/**
 * Сводка «сколько ещё разносить» — для баннера на списке и счётчика вкладки.
 * `enabled` — чтобы не дёргать API у пользователя без права разбора.
 */
export function useReviewSummary(
  entityType: ReviewEntityType,
  enabled = true,
): UseQueryResult<ReviewSummary> {
  return useQuery({
    queryKey: reviewKeys.summary(entityType),
    queryFn: () => api.get<ReviewSummary>(`${BASE}/${entityType}/summary`),
    enabled,
    staleTime: 60_000,
  });
}

export function useReviewBatches(
  entityType: ReviewEntityType,
  enabled = true,
): UseQueryResult<ReviewBatch[]> {
  return useQuery({
    queryKey: reviewKeys.batches(entityType),
    queryFn: () => api.get<ReviewBatch[]>(`${BASE}/${entityType}/batches`),
    enabled,
  });
}

export function useReviewBatch(
  entityType: ReviewEntityType,
  batchId: string | null,
): UseQueryResult<ReviewBatch> {
  return useQuery({
    queryKey: reviewKeys.batch(entityType, batchId ?? ''),
    queryFn: () =>
      api.get<ReviewBatch>(`${BASE}/${entityType}/batches/${batchId}`),
    enabled: batchId !== null,
  });
}

/**
 * Размер страницы строк мастера.
 *
 * Порция реального размера — сотни названий (ретро-порция тех.операций 1С —
 * 641), а сервер отдаёт максимум 500 за запрос. Поэтому страницами: иначе
 * строки за пределом первой выдачи недостижимы НИКАКИМ действием, порция
 * никогда не закроется, а экран об этом даже не скажет.
 */
export const ITEMS_PAGE_SIZE = 100;

/**
 * Строки вкладки мастера, ПОСТРАНИЧНО. `bucket=null` — все (шаг «Что пришло»).
 *
 * Следующая страница запрашивается, только пока предыдущая пришла полной —
 * так последняя (неполная) страница не порождает лишний запрос.
 */
export function useReviewItems(
  entityType: ReviewEntityType,
  batchId: string | null,
  bucket: ItemBucket | null,
  decision?: ItemDecision | null,
): UseInfiniteQueryResult<{ pages: ReviewItem[][] }, Error> {
  const base = new URLSearchParams();
  if (bucket) base.set('bucket', bucket);
  if (decision) base.set('decision', decision);
  return useInfiniteQuery({
    queryKey: reviewKeys.items(
      entityType,
      batchId ?? '',
      `${bucket ?? 'all'}:${decision ?? 'any'}`,
    ),
    initialPageParam: 0,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams(base);
      params.set('limit', String(ITEMS_PAGE_SIZE));
      params.set('offset', String(pageParam));
      return api.get<ReviewItem[]>(
        `${BASE}/${entityType}/batches/${batchId}/items?${params.toString()}`,
      );
    },
    getNextPageParam: (last: ReviewItem[], all: ReviewItem[][]) =>
      last.length === ITEMS_PAGE_SIZE ? all.flat().length : undefined,
    enabled: batchId !== null,
  }) as UseInfiniteQueryResult<{ pages: ReviewItem[][] }, Error>;
}

export function useDeclaration(
  entityType: ReviewEntityType,
  companyId: string | null,
  enabled = true,
): UseQueryResult<SufficiencyDeclaration> {
  return useQuery({
    queryKey: reviewKeys.declaration(entityType, companyId),
    queryFn: () =>
      api.get<SufficiencyDeclaration>(
        `${BASE}/${entityType}/declaration?company_id=${companyId}`,
      ),
    enabled: enabled && companyId !== null,
    staleTime: 60_000,
  });
}

// ─── запись ──────────────────────────────────────────────────────────────────

/**
 * Инвалидация после любого решения: строки, шапка порции, список порций и
 * сводка. Счётчики четырёх чисел живут на сервере (снимок порции), поэтому
 * пересчитывать их на клиенте нельзя — только перезапрашивать.
 */
function useReviewInvalidate(entityType: ReviewEntityType, batchId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['catalog-review', entityType] });
    void qc.invalidateQueries({
      queryKey: reviewKeys.batch(entityType, batchId),
    });
  };
}

export function useAgreeItem(entityType: ReviewEntityType, batchId: string) {
  const invalidate = useReviewInvalidate(entityType, batchId);
  return useMutation({
    mutationFn: (itemId: string) =>
      api.post<ReviewItem>(`${BASE}/${entityType}/items/${itemId}/agree`, {}),
    onSuccess: invalidate,
  });
}

export function useReplaceItem(entityType: ReviewEntityType, batchId: string) {
  const invalidate = useReviewInvalidate(entityType, batchId);
  return useMutation({
    mutationFn: (vars: { itemId: string; payload: ReplacePayload }) =>
      api.post<ReviewItem>(
        `${BASE}/${entityType}/items/${vars.itemId}/replace`,
        vars.payload,
      ),
    onSuccess: invalidate,
  });
}

/** «Это не она» — перенос строки сверки в ручной разбор (решение З). */
export function useDisputeItem(entityType: ReviewEntityType, batchId: string) {
  const invalidate = useReviewInvalidate(entityType, batchId);
  return useMutation({
    mutationFn: (vars: { itemId: string; note?: string | null }) =>
      api.post<ReviewItem>(
        `${BASE}/${entityType}/items/${vars.itemId}/dispute`,
        { note: vars.note ?? null },
      ),
    onSuccess: invalidate,
  });
}

export function useUndoItem(entityType: ReviewEntityType, batchId: string) {
  const invalidate = useReviewInvalidate(entityType, batchId);
  return useMutation({
    mutationFn: (itemId: string) =>
      api.post<ReviewItem>(`${BASE}/${entityType}/items/${itemId}/undo`, {}),
    onSuccess: invalidate,
  });
}

/** «Согласиться со всеми оставшимися (N)» — решение В. */
export function useAgreeAll(entityType: ReviewEntityType, batchId: string) {
  const invalidate = useReviewInvalidate(entityType, batchId);
  return useMutation({
    mutationFn: () =>
      api.post<{ agreed: number; counts: BatchCounts }>(
        `${BASE}/${entityType}/batches/${batchId}/agree-all`,
        {},
      ),
    onSuccess: invalidate,
  });
}

/**
 * «Начать разбор». Заодно сверяет порцию, которую ещё ни разу не сверяли —
 * ретро-порцию миграция заливает без прогона резолвера, и без этого админ
 * увидел бы «нужен человек» вместо предложений платформы.
 */
export function useStartBatch(entityType: ReviewEntityType, batchId: string) {
  const invalidate = useReviewInvalidate(entityType, batchId);
  return useMutation({
    mutationFn: () =>
      api.post<ReviewBatch>(
        `${BASE}/${entityType}/batches/${batchId}/start`,
        {},
      ),
    onSuccess: invalidate,
  });
}

export function useDeferBatch(entityType: ReviewEntityType, batchId: string) {
  const invalidate = useReviewInvalidate(entityType, batchId);
  return useMutation({
    mutationFn: () =>
      api.post<ReviewBatch>(
        `${BASE}/${entityType}/batches/${batchId}/defer`,
        {},
      ),
    onSuccess: invalidate,
  });
}

export function useCompleteBatch(entityType: ReviewEntityType, batchId: string) {
  const invalidate = useReviewInvalidate(entityType, batchId);
  return useMutation({
    mutationFn: () =>
      api.post<CompleteResult>(
        `${BASE}/${entityType}/batches/${batchId}/complete`,
        {},
      ),
    onSuccess: invalidate,
  });
}

export function useSetDeclaration(entityType: ReviewEntityType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      companyId: string;
      state: 'sufficient' | 'needs_more';
      note?: string | null;
    }) =>
      api.put<SufficiencyDeclaration>(`${BASE}/${entityType}/declaration`, {
        company_id: vars.companyId,
        state: vars.state,
        note: vars.note ?? null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['catalog-review', entityType] });
    },
  });
}
